import { createOnnxSession } from "@instacover/helpers"
import { useQuery } from "@tanstack/react-query"
import { useEffect } from "react"

import { queryKeys } from "../../constants/queryKeys"
import { useRemoteLogger } from "../../helpers/hooks/useRemoteLogger"
import { logger } from "../../utils/monitoring/logging"
import { captureSentryMessage } from "../../utils/monitoring/monitoring"

type UseOnnxModelParams = {
  modelUrl: string
  enabled: boolean
}

export function useOnnxModel(params: UseOnnxModelParams) {
  const { modelUrl, enabled } = params

  const { loadLog } = useRemoteLogger()

  const query = useQuery({
    queryKey: queryKeys.browserModel(modelUrl),
    queryFn: async () => {
      try {
        const loadStart = new Date().valueOf()
        const thresholdsUrl = modelUrl.replace(/\.onnx$/, ".quality_thresholds.json")
        logger.debug(`Loading quality thresholds from ${thresholdsUrl}`)
        const thresholdsPromise = fetch(thresholdsUrl)
          .then((r) => (r.ok ? (r.json() as Promise<Record<number, number>>) : null))
          .catch(() => null)

        const { session, Tensor } = await createOnnxSession(modelUrl)

        const loadEnd = new Date().valueOf() - loadStart
        logger.debug(`Loaded model in ${loadEnd}ms`)

        const thresholds = await thresholdsPromise
        logger.debug(`Quality thresholds ${thresholds ? "loaded" : "not found, using defaults"}: ${thresholdsUrl}`)

        const warmUpStart = new Date().valueOf()
        const arr = new Array(299 * 299 * 3).fill(0)
        const tensor = new Tensor(Int32Array.from(arr), [1, 299, 299, 3])
        await session.run({ input: tensor })
        const warmUpEnd = new Date().valueOf() - warmUpStart
        logger.debug(`Warmed up model in ${warmUpEnd}ms`)

        return { model: session, loadTime: loadEnd, warmUpTime: warmUpEnd, thresholds }
      } catch (error) {
        logger.error({ error }, "Failed to load model.")
        throw error
      }
    },
    refetchOnMount: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    gcTime: Infinity,
    enabled,
    retry: Infinity,
    retryDelay: 2000,
  })

  useEffect(() => {
    if (query.isSuccess) {
      loadLog({ userAgent: window.navigator.userAgent, buildHash: process.env.BUILD_HASH, modelUrl })
    }
  }, [loadLog, modelUrl, query.isSuccess])

  // React Query s retry: Infinity nikdy nevstoupí do isError stavu, error tedy nikdy nedorazí
  // do globálního Sentry handleru. Zachytáváme selhání explicitně přes captureMessage
  // (ne captureException) aby nebyl filtrován ignoreErrors pravidlem "Load failed".
  // Reportujeme až po 3. selhání — transientní chyby sítě se typicky vyřeší dříve.
  useEffect(() => {
    if (query.failureCount === 3 && query.failureReason) {
      const message = query.failureReason instanceof Error ? query.failureReason.message : String(query.failureReason)
      captureSentryMessage({ message: `Model load failed: ${message}`, level: "error", extra: { modelUrl } })
    }
  }, [query.failureCount, query.failureReason, modelUrl])

  return query
}
